import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { todayISO } from '../domain/clock';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { ANALYTICS, trackEvent } from '../lib/analytics';
import { useCollectionWindow } from './useCollectionWindow';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import { reportWaiting, runReport } from '../domain/reportRunner';
import { reportRequestDocument, sortReports } from '../domain/reports';
import { useStudents } from './useStudents';
import { useFamilies } from './useFamilies';
import { useLedger } from './useLedger';
import { useMakeup } from './useMakeup';
import { useEvents } from './useEvents';
import { useAttendance } from './useAttendance';
import { usePractice } from './usePractice';
import { useRepertoire } from './useRepertoire';
import { useMileage } from './useMileage';
import { useExpenses } from './useExpenses';
import { useSettings } from './useSettings';
import { currencyFor, deriveLocale, distanceUnitFor, formatTime, shortDatePattern } from '../domain/locale';
import { teacherDisplayName } from '../domain/people';
import { mileageSettingsOf } from '../domain/studio';

// Reports, run on this device and stored finished.
//
// `createReport` runs the report through domain/reportRunner.js over the lists
// this device has read, and writes a `reportRequests` document carrying its
// own result — ready with its table, or refused with its reason. No Cloud
// Function consumes it; the server reads one only to link its PDF
// (functions/documents/). Offline, the write waits in the queue like any
// other.

const ReportsContext = createContext(null);
const TRACK_LIMIT = 500;

export function ReportsProvider({ children }) {
  const { user } = useAuth();
  const { students, loading: studentsLoading } = useStudents();
  const { families, loading: familiesLoading, contacts, contactsLoading } = useFamilies();
  const { entries: ledgerEntries, loading: ledgerLoading } = useLedger();
  const { entries: makeupEntries, loading: makeupLoading } = useMakeup();
  const { occurrencesBetween, loading: seriesLoading, overridesLoading } = useEvents();
  const { statusFor, recordFor, loading: attendanceLoading } = useAttendance();
  const { entries: practiceEntries, loading: practiceLoading } = usePractice();
  const { items: repertoire, loading: repertoireLoading } = useRepertoire();
  const { nameFormats, settings, locale, loading: settingsLoading } = useSettings();
  const makeupPolicy = settings.makeup;
  // The studio's currency, from its Region, as every other amount on screen
  // is written: a report's money cells are written to its decimal places, so
  // a yen studio's report is not read as dollars and cents.
  const currency = currencyFor(locale);
  // The Mileage Log's trips, and the unit the Mileage report converts them to
  // — the studio's, from the same Region (#476).
  const { trips: mileageEntries, loading: mileageLoading } = useMileage();
  const distanceUnit = distanceUnitFor(locale);
  // Business Settings' Mileage Log Rate, which the report writes beside a
  // trip's Cost where it makes that Cost (#521).
  const mileageRate = mileageSettingsOf(settings).rateTenThousandths;
  // And its short date, which a report's dates are written in (#502), and
  // its clock, which Lessons Provided's Time is (#618).
  const datePattern = shortDatePattern(locale);
  const timeText = useCallback((hhmm) => formatTime(hhmm, locale), [locale]);
  const { entries: expenseEntries, loading: expensesLoading } = useExpenses();
  // The teacher's name in the studio's Teacher Name Format (#797), else the
  // studio's name: it heads Attendance and names Billable Hours' Teacher row.
  // And the Region's first day of the week, a Lessons Provided week's (#795).
  const teacherName = teacherDisplayName(settings, user?.displayName) || settings.name || null;
  const weekStartsOn = deriveLocale(locale).weekStartsOn;

  // The lists this device has read, by the core's names for them (#646). A
  // report is stored finished and cannot be updated, so one folded over a list
  // that has not answered yet — the seconds after a cold start — is wrong for
  // good. `waitingFor` is what the form asks before it enables Create, and
  // `createReport` asks it again and refuses: the form is the courtesy, this
  // is the gate. A list holding only a write queued before its first answer is
  // not read here, though a screen shows it: `loading`, not `reading`.
  const read = useMemo(() => Object.entries({
    students: studentsLoading,
    families: familiesLoading,
    contacts: contactsLoading,
    ledger: ledgerLoading,
    makeup: makeupLoading,
    schedule: seriesLoading || overridesLoading,
    attendance: attendanceLoading,
    practice: practiceLoading,
    repertoire: repertoireLoading,
    mileage: mileageLoading,
    expenses: expensesLoading,
    settings: settingsLoading,
  }).filter(([, loading]) => loading === false).map(([name]) => name), [
    studentsLoading, familiesLoading, contactsLoading, ledgerLoading, makeupLoading, seriesLoading, overridesLoading,
    attendanceLoading, practiceLoading, repertoireLoading, mileageLoading, expensesLoading, settingsLoading,
  ]);

  // The reportRequests window (#207 U5).
  const reportWindow = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.reportRequests,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const loading = reportWindow.loading;
  // Sorted on a copy: `docs` is the hook's own memoised array, and sorting it
  // in place would reorder what every other reader of that window sees.
  const reports = useMemo(() => sortReports(reportWindow.docs), [reportWindow.docs]);

  const value = useMemo(() => ({
    loading,
    // The read stopped before it answered (#647) — src/domain/reading.js.
    failed: reportWindow.failed,
    reports,
    // `{ waiting, reason }` — `reportWaiting` over what this device has read.
    waitingFor: (typeId, params) => reportWaiting(typeId, params, read),
    createReport: async ({ typeId, label, params }) => {
      if (!user) return null;
      // Refused, and nothing written, while a list the report reads is unread.
      const { reason } = reportWaiting(typeId, params, read);
      if (reason) throw new Error(reason);
      const id = freeId('rpt');

      // The runner expands the series itself, over the window the report
      // reads and no further — none for a type that reads no lesson (#625).
      const result = runReport(typeId, params, {
        students, families, ledgerEntries, makeupEntries, occurrencesBetween, statusFor, recordFor,
        practiceEntries, repertoire,
        // A report is filed with an accountant, so the names in it are written
        // the way the studio writes names everywhere else.
        nameFormats,
        // The make-up credit cap and expiry window likewise: the student list
        // and the Make-Up Credits report fold the same ledger and must agree.
        makeupPolicy,
        currency,
        mileageEntries,
        distanceUnit,
        mileageRate,
        datePattern,
        timeText,
        // Revenue & Expenses covers expenses and other revenue, as the
        // Expenses page does (#503).
        expenseEntries,
        // Student Details' Email and Phone (#792), Attendance's heading and
        // a Lessons Provided week (#795).
        contacts,
        teacherName,
        weekStartsOn,
      });

      const batch = writeBatch(db);
      // `reportRequestDocument` (src/domain/reports.js): ready with its
      // table or refused with its reason, and the business date, so the list
      // orders deterministically even before serverTimestamp resolves.
      batch.set(
        docRef(user.uid, COLLECTIONS.reportRequests, id),
        reportRequestDocument({ typeId, label, params, result, createdOn: todayISO() }, serverTimestamp()),
      );
      await batch.commit();
      // Which report, by its fixed id — never its label, range or note (#458).
      trackEvent(ANALYTICS.reportRun, { report_type: typeId });
      return id;
    },
  }), [reports, loading, reportWindow.failed, read, user, students, families, ledgerEntries, makeupEntries, occurrencesBetween, statusFor, practiceEntries, repertoire, nameFormats, makeupPolicy, currency, mileageEntries, distanceUnit, mileageRate, datePattern, timeText, expenseEntries, recordFor, contacts, teacherName, weekStartsOn]);

  return <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>;
}

export function useReports() {
  const ctx = useContext(ReportsContext);
  if (!ctx) throw new Error('useReports must be used within a ReportsProvider');
  return ctx;
}
